/**
 * UUID Generation Utility
 *
 * Provides a cross-environment UUID generator that works in browsers,
 * Node.js, and production builds where crypto.randomUUID may not be available.
 */

/**
 * Generate a UUID v4 that works in all environments
 *
 * Tries to use native crypto.randomUUID if available, otherwise falls back
 * to a Math.random()-based implementation.
 *
 * @returns A valid UUID v4 string
 */
export function generateUUID(): string {
  // Try native crypto.randomUUID first (available in modern browsers and Node 15.6+)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch (e) {
      // Fall through to polyfill if crypto.randomUUID exists but throws
      console.warn('crypto.randomUUID failed, using fallback:', e);
    }
  }

  // Fallback: Simple UUID v4 implementation using Math.random()
  // Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  // Where 4 indicates version 4 and y is one of 8, 9, A, or B
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}
